"use client";

import { useEffect, useState } from "react";
import { useLanguage } from "../../app/components/context/LanguageContext";
import { useTheme } from "../../app/context/ThemeContext";
import Link from "next/link";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";







/* ===================== TYPES ===================== */

// ---------- NEWS ----------
interface FeaturedImage {
  url: string;
  alternativeText?: string;
}

interface SummaryParagraph {
  type: string;
  children: { text: string }[];
}

interface NewsItem {
  id: number;
  title: string;
  slug: string;
  summary: SummaryParagraph[];
  featuredImage: FeaturedImage[];
  category?: string;
}

// ---------- PUBLIC TENDERS ----------
interface Document {
  name: string;
  url: string;
}

interface Location {
  LocationName: string;
}

interface Tender {
  id: number;
  IDs?: number;
    number?: string;

  title: string;
  slug: string;
  modedepassation: string;
  typeofreview?: string | null;
  DatedeParution?: string | null;
  DateLimitedeSoumission?: string | null;
  Documents?: Document | null;
  LieuDeRetraitDuCahierDesCharges?: Location | null;
  LieudeDepotdesOffres?: Location | null;
}
const translations: any = {
  fr: {
    label1: "Accueil",
    label2: "Appels d’offres",
    Loading: "Chargement ...",
    Expiré: "Expiré",
    Publication: "Date de publication :",
    Date_limite: "Date limite :",
    Document_disponible: "Document disponible",
    Retrait: "Retrait du dossier :",
    Dépôt: "Dépôt des offres :",
    Voir_les_détails: "Voir les détails →",
  },

  en: {
    label1: "Home",
    label2: "Public tenders",
    Loading: "Loading ...",
    Expiré: "Expired",
    Publication: "Publication date:",
    Date_limite: "Deadline:",
    Document_disponible: "Document available",
    Retrait: "File withdrawal:",
    Dépôt: "Bid submission:",
    Voir_les_détails: "View details →",
  },

  ar: {
    label1: "الرئيسية",
    label2: "المناقصات العمومية",
    Loading: "جارٍ التحميل ...",
    Expiré: "منتهي الصلاحية",
    Publication: "تاريخ النشر:",
    Date_limite: "آخر أجل:",
    Document_disponible: "الوثائق متوفرة",
    Retrait: "سحب الملف:",
    Dépôt: "إيداع العروض:",
    Voir_les_détails: "عرض التفاصيل ←",
  },
};



/* ===================== COMPONENT ===================== */

export default function NewsAndPublicTendersSection() {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const [news, setNews] = useState<NewsItem[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [loading, setLoading] = useState(true);
  const t = translations[language] || translations.en;

  /* ===================== FETCH NEWS ===================== */
  useEffect(() => {
    async function fetchNews() {
   const newsRes = await fetch(
          `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/newsses?populate=*`
        );
      const data = await newsRes.json();
      setNews(data.data || []);
    }
    fetchNews();
  }, [language]);

  /* ===================== FETCH TENDERS =====================  
  useEffect(() => {
        let isMounted = true;

    async function fetchTenders() {
      const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL;
      if (!STRAPI_URL) return;

      const res = await fetch(
        `${STRAPI_URL}/api/publictendersses?populate=*&locale=${language}`
      );
      const data = await res.json();
      setTenders(data.data || []);
      setLoading(false);
    }
    fetchTenders();
     return () => {
      isMounted = false;
    };
  }, [language]);

*/


    useEffect(() => {
  let isMounted = true;

  async function fetchTenders() {
    try {
    const res = await fetch(`/api/publictenders?locale=${language}`);
        const data = await res.json();

         if (isMounted) setTenders(data.data || []);
      } catch (err) {
        console.error("Impossible de charger les appels d'offres:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
  }

    fetchTenders();
    return () => {
      isMounted = false;
    };
  }, [language]); 



  const lastSixTenders = [...tenders]
    .sort((a, b) => b.id - a.id)
    .slice(0, 6);

  return (
    <section
      className={`py-24 ${
        theme === "dark"
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900/20"
          : "bg-gradient-to-br from-gray-50 via-white to-indigo-50/30"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 space-y-32">

        {/* ===================== NEWS ===================== */}

        <div>
          <h2 className="text-4xl font-bold text-center mb-12">
            {language === "fr" && "Actualités Récentes"}
            {language === "en" && "Recent News"}
            {language === "ar" && "آخر الأخبار"}
          </h2>

          <Swiper
            modules={[Autoplay]}
            loop
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            spaceBetween={30}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 4 },
            }}
          >
            {news.map((item) => {
              const imageUrl = item.featuredImage?.[0]?.url
                ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${item.featuredImage[0].url}`
                : "/placeholder.jpg";

              const summary =
                item.summary
                  ?.map((p) => p.children.map((c) => c.text).join(""))
                  .join(" ")
                  .substring(0, 120) + "...";

              return (
                <SwiperSlide key={item.id}>
                  <Link href={`/news/${item.slug}`} className="block h-full">
                    <div
                      className={`rounded-3xl overflow-hidden h-full shadow-xl transition hover:scale-105 ${
                        theme === "dark" ? "bg-gray-800" : "bg-white"
                      }`}
                    >
                      <img
                        src={imageUrl}
                        alt={item.title}
                        className="w-full h-48 object-cover"
                      />
                      <div className="p-6">
                        <h3 className="font-bold mb-2 line-clamp-2">
                          {item.title}
                        </h3>
                        <p className="text-sm line-clamp-3">{summary}</p>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
                 <div className="text-center mt-12">
            <Link href="/news">
              <button
                className={`  px-6  py-3 rounded-xl font-semibold ${
                  theme === "dark" ? "bg-blue-600 text-white" : "bg-gray-900 text-white"
                }`}
              >
               
                 {language === "fr" && "Voir plus news →"}
            {language === "en" && "Latest news "}
            {language === "ar" && " عرض المزيد من الأخبار  "}
              </button>
            </Link>
          </div>
        </div>

        {/* ===================== PUBLIC TENDERS ===================== */}

        <div>
          <h2 className="text-4xl font-bold text-center mb-12">
            {language === "fr" && "Derniers appels d’offres"}
            {language === "en" && "Latest Public Tenders"}
            {language === "ar" && "أحدث المناقصات"}
          </h2>

          <Swiper
          
            modules={[Autoplay]}
            loop
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            spaceBetween={30}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 4 },
            }}
          >
            {lastSixTenders.map((tender) => (
              <SwiperSlide key={tender.id}>
                <TenderCard tender={tender} theme={theme} />
              </SwiperSlide>
            ))}
          </Swiper>
 


          <div className="text-center mt-12">
            <Link href="/publictenders">
              <button
                className={`  px-6  py-3 rounded-xl font-semibold ${
                  theme === "dark" ? "bg-blue-600 text-white" : "bg-gray-900 text-white"
                }`}
              >
           {language === "fr" && "Voir plus d’appels d’offres →"}
            {language === "en" && "See more tenders →"}
            {language === "ar" && "عرض المزيد من المناقصات →"}              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===================== YOUR TenderCard (UNCHANGED) ===================== */
/*
function TenderCard({ tender, theme }: { tender: Tender; theme: string }) {
  const cardBg =
    theme === "dark"
      ? "bg-gray-800 border-gray-700 hover:border-blue-500"
      : "bg-white border-gray-200 hover:border-blue-200";

  const textPrimary = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const textSecondary = theme === "dark" ? "text-gray-400" : "text-gray-600";

  const isExpired = tender.DateLimitedeSoumission
    ? new Date(tender.DateLimitedeSoumission) < new Date()
    : false;

  return (
    <Link href={`/publictenders/${tender.slug}`} className="block group">
      <div
        className={`${cardBg} rounded-xl shadow-lg border transition-all duration-300 p-6 h-full`}
      >

        <div className="flex justify-between items-start mb-4">
          <span className="text-lg font-bold text-blue-600">
            #{tender.IDs || tender.id}
          </span>

          {isExpired && (
            <span className="bg-red-100 text-red-700 text-xs font-semibold px-3 py-1 rounded-full border border-red-300">
              Expiré
            </span>
          )}
        </div>

        <div className="mb-4">
          <h2 className={`text-xl font-bold ${textPrimary}`}>
            {tender.modedepassation}
          </h2>

          <p className="text-sm text-blue-600 font-medium mb-1">
            {tender.typeofreview || "Avis de publication"}
          </p>

          <h3 className={`text-lg font-semibold line-clamp-2 ${textPrimary}`}>
            {tender.title}
          </h3>
        </div>

        <div className="space-y-2 mb-4 text-sm">
          <p className={textSecondary}>
            📅 <span className="font-semibold">Publication:</span>{" "}
            {tender.DatedeParution}
          </p>

          <p className={isExpired ? "text-red-500 font-semibold" : textSecondary}>
            ⏳ <span className="font-semibold">Date limite:</span>{" "}
            {tender.DateLimitedeSoumission}
          </p>
        </div>

        {tender.Documents && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm">
            📄 Document disponible
          </div>
        )}

        <div className={`space-y-2 mb-4 text-xs ${textSecondary}`}>
          {tender.LieuDeRetraitDuCahierDesCharges && (
            <p>
              📍 <strong>Retrait:</strong>{" "}
              {tender.LieuDeRetraitDuCahierDesCharges.LocationName}
            </p>
          )}
          {tender.LieudeDepotdesOffres && (
            <p>
              📍 <strong>Dépôt:</strong>{" "}
              {tender.LieudeDepotdesOffres.LocationName}
            </p>
          )}
        </div>

        <hr className="my-4 border-gray-300 dark:border-gray-700" />

        <div className="flex justify-between items-center">
          <span
            className={`text-sm font-semibold ${
              isExpired ? "text-red-600" : "text-green-600"
            }`}
          >
            {isExpired ? "Expiré" : "Actif"}
          </span>

          <span className="text-blue-600 dark:text-blue-400 group-hover:underline font-semibold text-sm">
            Voir les détails →
          </span>
        </div>
      </div>
    </Link>
  );
}

*/
function TenderCard({ tender, theme }: { tender: Tender; theme: string }) {
  const cardBg =
    theme === "dark"
      ? "bg-gray-800 border-gray-700 hover:border-blue-500"
      : "bg-white border-gray-200 hover:border-blue-200";

  const textPrimary = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const textSecondary = theme === "dark" ? "text-gray-400" : "text-gray-600";

  const isExpired = tender.DateLimitedeSoumission
    ? new Date(tender.DateLimitedeSoumission) < new Date()
    : false;

  return (
    <Link href={`/publictenders/${tender.slug}`} className="block h-full">
      <div
        className={`${cardBg} rounded-xl shadow-lg border transition-all duration-300 p-6 flex flex-col`}
      >
        {/* ================= TOP ================= */}
        <div className="flex-1">
          <div className="flex justify-between items-start mb-4">
            <span className="text-lg font-bold text-blue-600">
              #{tender.number || tender.number}
            </span>

            {isExpired && (
              <span className="bg-red-100 text-red-700 text-xs font-semibold px-3 py-1 rounded-full border border-red-300">
                {translations.Expiré}
              </span>
            )}
          </div>

          <div className="mb-4 space-y-1">
            <h2 className={`text-xl font-bold ${textPrimary}`}>
              {tender.modedepassation}
            </h2>

            <p className="text-sm text-blue-600 font-medium">
              {tender.typeofreview || "Avis de publication"}
            </p>

            <h3 className={`text-lg font-semibold ${textPrimary}`}>
              {tender.title}
            </h3>
          </div>

          <div className="space-y-2 mb-4 text-sm">
            <p className={textSecondary}>
              📅 <span className="font-semibold">{translations.Publication} :</span>{" "}
              {tender.DatedeParution}
            </p>

            <p
              className={
                isExpired ? "text-red-500 font-semibold" : textSecondary
              }
            >
              ⏳ <span className="font-semibold">{translations.Date_limite } :</span>{" "}
              {tender.DateLimitedeSoumission}
            </p>
          </div>

          {tender.Documents && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm">
              📄{translations.Document_disponible} 
            </div>
          )}

          <div className={`space-y-2 text-xs ${textSecondary}`}>
            {tender.LieuDeRetraitDuCahierDesCharges && (
              <p>
                📍 <strong>{translations.retrait } :</strong>{" "}
                {tender.LieuDeRetraitDuCahierDesCharges.LocationName}
              </p>
            )}
            {tender.LieudeDepotdesOffres && (
              <p>
                📍 <strong>📄{translations.Dépôt} :</strong>{" "}
                {tender.LieudeDepotdesOffres.LocationName}
              </p>
            )}
          </div>
        </div>

        {/* ================= BOTTOM ================= */}
        <div className="mt-4">
          <hr className="my-4 border-gray-300 dark:border-gray-700" />

          <div className="flex justify-between items-center">
            <span
              className={`text-sm font-semibold ${
                isExpired ? "text-red-600" : "text-green-600"
              }`}
            >
              {isExpired ? "Expiré" : "Actif"}
            </span>

            <span className="text-blue-600 dark:text-blue-400 hover:underline font-semibold text-sm">
              📄{translations.Voir_les_détails} 
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}



















 